/**
 * One editable cell.
 *
 * Editing commits explicitly — Enter, or the tick — rather than on blur.
 * Blur-to-save gave no confirmation that anything had happened and no way to
 * back out, so a mis-click silently wrote a value.
 *
 * Where a user classification disagrees with the source, the cell carries a
 * marker that names both sides:
 *
 *     1C                 FINANCE EXPENSES
 *     Artem Stopnevich   IT Infrastructure      13 Aug 2026
 */

import { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { PenLine, Check, X, Loader2 } from 'lucide-react';
import { ReferencePicker, type Option } from './ReferencePicker';

/**
 * The stored type ids. The staff-facing names and the picker live on the
 * server (api/_lib/attribute-types.ts) so the vocabulary is declared once;
 * this union is only what the renderer switches on.
 */
export type AttributeTypeId =
  | 'text' | 'number' | 'currency' | 'date' | 'datetime' | 'boolean'
  | 'select' | 'multi_select' | 'status' | 'rating' | 'reference'
  | 'email' | 'phone' | 'url';

export interface Attribute {
  api_slug: string;
  label: string;
  type: AttributeTypeId;
  ownership: 'source' | 'user' | 'derived';
  is_required: boolean;
  options: string[] | null;
  references_object: string | null;
  /** Which currency a Currency attribute is counted in. */
  currency_code?: string | null;
  /** A note shown to whoever fills the field in. */
  help_text?: string | null;
}

/** Types whose value is a list of choices rather than one scalar. */
export const isMulti = (t: AttributeTypeId): boolean => t === 'multi_select';

/**
 * Tag colours.
 *
 * Assigned by hashing the tag text, not by position in the option list, so a
 * tag keeps its colour when the list is reordered or another choice is added
 * above it — and the same tag reads the same colour in every table it appears
 * in. Ten hues, each with a light and a dark variant, all at contrast that
 * survives both themes.
 */
const TAG_COLOURS = [
  'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
  'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
  'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
  'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-300',
  'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
  'bg-cyan-100 text-cyan-900 dark:bg-cyan-500/20 dark:text-cyan-300',
  'bg-lime-100 text-lime-900 dark:bg-lime-500/20 dark:text-lime-300',
  'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-500/20 dark:text-fuchsia-300',
  'bg-orange-100 text-orange-900 dark:bg-orange-500/20 dark:text-orange-300',
  'bg-teal-100 text-teal-900 dark:bg-teal-500/20 dark:text-teal-300',
];

export function tagColour(value: string): string {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) >>> 0;
  return TAG_COLOURS[h % TAG_COLOURS.length];
}

/** A tag pill. Shared by the cell, the editor and the record panel. */
export function Tag({ value, className }: { value: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium leading-none',
        tagColour(value), className
      )}
    >
      {value}
    </span>
  );
}

/**
 * Multi-values are stored as a JSON array in the same text column a single
 * value would use. Reads are lenient: a column switched from Dropdown to
 * Labels still holds bare strings, and blanking those on read would look like
 * data loss.
 */
export function parseMulti(raw: unknown): string[] {
  if (raw === null || raw === undefined || raw === '') return [];
  if (Array.isArray(raw)) return raw.map(String);
  const s = String(raw);
  if (s.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      /* not JSON — fall through to the comma split */
    }
  }
  return s.split(',').map((v) => v.trim()).filter(Boolean);
}

export interface Override {
  value: string | null;
  by: string | null;
  at: string | null;
}

interface Props {
  attr: Attribute;
  sourceValue: unknown;
  override?: Override;
  sourceSystem: string | null;
  editable: boolean;
  /**
   * Labels for the reference values on this page, so a cell can show a name
   * rather than a key. The picker fetches its own options — this is display
   * only, and is exact for the visible rows.
   */
  lookup?: Option[];
  onCommit: (value: string | null) => Promise<void>;
}

/**
 * Which native input a type gets. The browser's own keyboard and validation
 * are better than anything hand-rolled here — a phone field on a tablet
 * should bring up the number pad.
 */
const INPUT_TYPE: Partial<Record<AttributeTypeId, string>> = {
  number: 'number',
  currency: 'number',
  rating: 'number',
  date: 'date',
  datetime: 'datetime-local',
  email: 'email',
  phone: 'tel',
  url: 'url',
};

function personName(email: string | null): string {
  if (!email) return 'Unknown';
  return email.split('@')[0].split(/[._-]/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function fmt(v: unknown, attr: Pick<Attribute, 'type' | 'currency_code'>): string {
  const { type } = attr;
  if (v === null || v === undefined || v === '') return '';
  if (type === 'currency') {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    // Without a currency the amount is ambiguous, so it falls back to a plain
    // formatted number rather than inventing a symbol. Settings → Fields is
    // where the currency gets set.
    return attr.currency_code
      ? n.toLocaleString('en-US', {
          style: 'currency', currency: attr.currency_code, maximumFractionDigits: 2,
        })
      : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  if (type === 'number') {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(v);
  }
  if (type === 'rating') {
    const n = Number(v);
    return Number.isFinite(n) ? '★'.repeat(Math.max(0, Math.min(5, Math.round(n)))) : String(v);
  }
  if (type === 'date') return String(v).slice(0, 10);
  if (type === 'datetime') {
    const d = new Date(String(v));
    return Number.isNaN(d.getTime())
      ? String(v)
      : d.toLocaleString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
  }
  if (type === 'boolean') return v ? 'Yes' : 'No';
  if (type === 'multi_select') return parseMulti(v).join(', ');
  return String(v);
}

export function EditableCell({
  attr, sourceValue, override, sourceSystem, editable, lookup, onCommit,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasOverride = override?.value !== undefined && override?.value !== null;
  const effective = hasOverride ? override!.value : sourceValue;
  // A reference stores a key; show the human label where we have one.
  const display = (v: unknown) =>
    lookup && v != null ? (lookup.find((o) => o.value === String(v))?.label ?? String(v)) : v;
  const sourceText = fmt(sourceValue, attr);
  const differs = hasOverride && String(override!.value ?? '') !== String(sourceValue ?? '');

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  if (!editable) {
    return (
      <div
        className="px-2 py-1 text-xs text-muted-foreground truncate"
        title={attr.ownership === 'derived'
          ? 'Calculated by the pipeline'
          : `From ${sourceSystem ?? 'the warehouse'}`}
      >
        {attr.type === 'boolean' ? (
          <Checkbox
            checked={sourceValue === true || sourceValue === 'true'}
            disabled
            className="h-3.5 w-3.5"
            aria-label={sourceValue ? 'Yes' : 'No'}
          />
        ) : attr.type === 'multi_select' ? (
          <span className="flex gap-1 overflow-hidden">
            {parseMulti(sourceValue).map((p) => <Tag key={p} value={p} />)}
          </span>
        ) : (
          fmt(display(sourceValue), attr)
        )}
      </div>
    );
  }

  const commit = async (next: string | null) => {
    if (String(next ?? '') === String(effective ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await onCommit(next);
      setEditing(false);
    } catch {
      /* the caller toasts; keep the editor open so the value is not lost */
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    // A select commits on choice — there is nothing to type, so an extra
    // confirmation step would only be friction.
    // A reference searches the target object; a select picks from a fixed list.
    if (attr.type === 'reference' && attr.references_object) {
      return (
        <Popover open onOpenChange={(o) => { if (!o) setEditing(false); }}>
          <PopoverTrigger asChild>
            {/* Keeps showing the current value: the popover sits beside the
                cell, and an empty box gives nothing to compare against. */}
            <div className="flex h-7 w-full items-center border border-primary px-2 text-xs truncate">
              {saving
                ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                : String(display(effective) ?? '') || (
                    <span className="italic text-muted-foreground/50">Empty</span>
                  )}
            </div>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <ReferencePicker
              object={attr.references_object}
              seed={lookup}
              current={effective == null ? null : String(effective)}
              allowClear={!attr.is_required}
              onPick={(v) => commit(v)}
              onCancel={() => setEditing(false)}
            />
          </PopoverContent>
        </Popover>
      );
    }

    // Labels hold several choices at once, so unlike a dropdown there is no
    // single pick that means "done" — it commits explicitly on Save.
    if (attr.type === 'multi_select' && attr.options) {
      const picked = parseMulti(effective);
      const toggle = (o: string) =>
        setDraft(JSON.stringify(
          picked.includes(o) ? picked.filter((p) => p !== o) : [...picked, o]
        ));
      const pending = draft ? parseMulti(draft) : picked;
      return (
        <Popover open onOpenChange={(o) => { if (!o) setEditing(false); }}>
          <PopoverTrigger asChild>
            <div className="flex h-7 w-full items-center gap-1 overflow-hidden border border-primary px-1.5">
              {pending.length
                ? pending.map((p) => <Tag key={p} value={p} />)
                : <span className="text-xs italic text-muted-foreground/50">Empty</span>}
            </div>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-0">
            <div className="max-h-64 overflow-y-auto py-1">
              {attr.options.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => toggle(o)}
                  className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-accent"
                >
                  <Checkbox checked={pending.includes(o)} className="pointer-events-none h-3 w-3" />
                  <Tag value={o} />
                </button>
              ))}
            </div>
            <Separator />
            <div className="flex justify-end gap-1 p-1">
              <Button size="sm" variant="ghost" className="h-6 text-xs"
                onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" className="h-6 text-xs" disabled={saving}
                onClick={() => commit(pending.length ? JSON.stringify(pending) : null)}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      );
    }

    if ((attr.type === 'select' || attr.type === 'status') && attr.options) {
      return (
        <Select
          defaultValue={String(effective ?? '')}
          onValueChange={(v) => commit(v)}
          open
          onOpenChange={(o) => { if (!o) setEditing(false); }}
        >
          <SelectTrigger className="h-7 rounded-none border-primary text-xs focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {attr.options.map((o) => (
              <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <div className="flex items-center gap-0.5 bg-background">
        <Input
          ref={inputRef}
          autoFocus
          disabled={saving}
          defaultValue={String(effective ?? '')}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(draft === '' ? null : draft); }
            if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
          }}
          type={INPUT_TYPE[attr.type] ?? 'text'}
          className="h-7 rounded-none border-primary text-xs focus-visible:ring-0"
        />
        <Button
          size="icon" variant="ghost" disabled={saving}
          aria-label="Save"
          className="h-6 w-6 shrink-0 text-emerald-600 hover:text-emerald-600"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => commit(draft === '' ? null : draft)}
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        </Button>
        <Button
          size="icon" variant="ghost" disabled={saving}
          aria-label="Cancel"
          className="h-6 w-6 shrink-0 text-muted-foreground"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setEditing(false)}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group/cell flex items-center gap-1 px-2 py-1 min-h-7">
      <button
        type="button"
        disabled={saving}
        onClick={() => {
          // A tick box has exactly two states, so a text editor with a save
          // step would be three clicks to express one bit. Toggle in place.
          if (attr.type === 'boolean') {
            const now = effective === true || effective === 'true';
            void commit(now ? 'false' : 'true');
            return;
          }
          setDraft(String(effective ?? ''));
          setEditing(true);
        }}
        className={cn(
          'flex-1 truncate rounded-sm px-0.5 -mx-0.5 text-left text-xs',
          'hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          !effective && 'italic text-muted-foreground/50'
        )}
      >
        {attr.type === 'multi_select' && parseMulti(effective).length ? (
          <span className="flex gap-1 overflow-hidden">
            {parseMulti(effective).map((p) => <Tag key={p} value={p} />)}
          </span>
        ) : attr.type === 'boolean' ? (
          // A tick box reads at a glance down a column; the words "Yes" and
          // "No" have to be read one row at a time.
          <Checkbox
            checked={effective === true || effective === 'true'}
            className="pointer-events-none h-3.5 w-3.5"
            aria-label={effective ? 'Yes' : 'No'}
          />
        ) : (
          fmt(display(effective), attr) || 'Empty'
        )}
      </button>

      {differs && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Show provenance"
              className="shrink-0 rounded-sm p-0.5 text-amber-600 hover:bg-amber-500/15 dark:text-amber-500"
            >
              <PenLine className="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0">
            <div className="px-3 py-2">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {attr.label}
              </p>
            </div>
            <Separator />
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-3 py-2 text-xs">
              <Badge variant="secondary" className="justify-self-start font-mono text-[10px]">
                {sourceSystem ?? 'Source'}
              </Badge>
              <span className={cn('font-mono', !sourceText && 'italic text-muted-foreground/50')}>
                {sourceText || 'empty'}
              </span>
              <Badge className="justify-self-start font-mono text-[10px]">
                {personName(override!.by)}
              </Badge>
              <span className="font-mono">{override!.value}</span>
            </div>
            {override!.at && (
              <>
                <Separator />
                <p className="px-3 py-1.5 text-[11px] text-muted-foreground">
                  Changed {new Date(override!.at).toLocaleDateString('en-GB',
                    { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
